import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useMemo } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, Search, Text } from '@/components/core';
import { ImportIcon, ReviewIcon, RotateIcon } from '@/components/icons';
import { toAgentRunId, useFilteredAgentRunsQuery, useInvalidateAgentRunQueries } from '@/hooks/api';
import { useInboxStore } from '@/store';

import { ImportModal } from './ImportModal';
import { InboxFilter } from './InboxFilter';
import { InboxSort } from './InboxSort';

export type RunsActionBarProps = {
  agentId: string;
};

const IRunsActionBar = ({ agentId }: RunsActionBarProps) => {
  const navigate = useNavigate();

  const { invalidate: invalidateAgentRuns } = useInvalidateAgentRunQueries();
  const { data } = useFilteredAgentRunsQuery({ agentId });
  const isFetching = useInboxStore((state) => state.isFetching);
  const setIsFetching = useInboxStore((state) => state.setIsFetching);

  const refreshAgentRuns = useCallback(() => {
    setIsFetching(true);
    invalidateAgentRuns();
  }, [invalidateAgentRuns, setIsFetching]);

  const search = useInboxStore((state) => state.search);
  const setSearch = useInboxStore((state) => state.setSearch);

  const totalWaitingForReview = useMemo(
    () => data?.pages?.flatMap((page) => page.data).filter((r) => r.status === 'ready-for-review').length ?? 0,
    [data]
  );

  const firstWaitingForReview = useMemo(
    () => data?.pages?.flatMap((page) => page.data).filter((r) => r.status === 'ready-for-review')[0],
    [data]
  );

  const onReview = useCallback(async () => {
    const agentRunId = toAgentRunId(agentId, firstWaitingForReview?.runId);
    if (agentRunId) {
      await navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: agentRunId,
        search: { showPanel: 'agent-runs' },
      });
    }
  }, [firstWaitingForReview?.runId, navigate, agentId]);

  const clearSearch = useCallback(() => setSearch(''), [setSearch]);

  return (
    <Flex className="gap-xs items-center">
      <Search
        clear={clearSearch}
        props={{
          input: {
            value: search,
            onChange: (e) => setSearch(e.target.value),
            placeholder: 'Search...',
          },
          searchField: { className: '' },
        }}
      />

      <InboxFilter />
      <InboxSort />

      <Button isDisabled={isFetching} variant="plain" onPress={refreshAgentRuns}>
        <RotateIcon className={merge('!text-gray-400', isFetching && '!text-primary-500 animate-spin')} rotation="cw" />
      </Button>

      <div className="grow" />

      <DialogTrigger>
        <Button variant="secondary">
          <ImportIcon />
          <Text>Import</Text>
        </Button>
        <ImportModal agentId={agentId} />
      </DialogTrigger>

      <Button variant="primary" isDisabled={totalWaitingForReview < 1} onPress={onReview}>
        <ReviewIcon />
        <Text>Review</Text>

        {totalWaitingForReview > 0 ? (
          <Text className="border-primary-200 py-sm px-xs flex h-3 items-center justify-center rounded-sm border-1 bg-white/20 text-xs font-medium">
            {totalWaitingForReview}
          </Text>
        ) : null}
      </Button>
    </Flex>
  );
};

export const RunsActionBar = memo(IRunsActionBar);
